import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import {
  SPS_PRUEFSTATUS_OPTIONEN,
  SpsLoeschErgebnis,
  SpsPruefprotokoll,
  SpsPruefstatus,
  SpsPruefstatusOption,
  SpsSignalDetail,
  SpsSignalFilter,
  SpsSignalNachbarn,
  SpsSignalQuelle,
  listenTypInfo,
  projektListenTypVonSps,
  pruefstatusOption,
  richtungText,
  spsFilterAlsParams,
  spsFilterAusParams
} from '../../../../../Models/Sps';
import { AuthService } from '../../../../../api-authorization/authorize.service';
import { SpsPruefungApiService } from '../../../../../Services/sps-pruefung-api.service';
import { SpsPruefungComponent } from '../sps-pruefung/sps-pruefung.component';
import { SpsSignalLoeschenDialogComponent, SpsSignalLoeschenDialogData } from '../sps-signal-loeschen-dialog/sps-signal-loeschen-dialog.component';

/** Ein Label/Wert-Paar der technischen Daten. */
export interface SpsDetailFeld {
  label: string;
  wert: string | null;
  mono?: boolean;
}

/**
 * Ausführliche Detailansicht EINES SPS-Signals: technische Daten, alle
 * Datenquellen (Originalzeilen beider Listen), Prüfstatus mit Bemerkung und
 * der vollständige Prüfverlauf.
 *
 * Statusänderungen laufen über dieselbe API wie die Schnellprüfung in der
 * Tabelle (PUT api/sps/signale/{id}/status, mit Version → 409 bei Konflikt).
 * „Vorheriges/Nächstes“ folgt dem Filter und der Sortierung der SPS-Prüfung,
 * die als Query-Parameter mitkommen (GET …/nachbarn).
 */
@Component({
  selector: 'app-sps-signal-detail',
  templateUrl: './sps-signal-detail.component.html',
  styleUrls: ['./sps-signal-detail.component.css']
})
export class SpsSignalDetailComponent implements OnInit, OnDestroy {

  projektId = 0;
  signalId = 0;

  signal: SpsSignalDetail | null = null;
  verlauf: SpsPruefprotokoll[] = [];
  nachbarn: SpsSignalNachbarn | null = null;

  readonly statusOptionen: SpsPruefstatusOption[] = SPS_PRUEFSTATUS_OPTIONEN;

  /** Entwurf der Prüfung – erst `speichern()` schickt ihn ans Backend. */
  entwurfStatus: SpsPruefstatus = 'Ungeprueft';
  entwurfBemerkung = '';

  laedt = false;
  laedtVerlauf = false;
  speichert = false;
  fehler = '';
  speicherFehler = '';
  gespeichertHinweis = '';

  /** True nach HTTP 409: ein anderer Benutzer hat zwischenzeitlich geprüft – neu laden. */
  versionskonflikt = false;

  /** Herkunft für „Zurück“: 'pruefung' (SPS-Prüfung), 'liste' (SPS-Liste) oder null (Projekt). */
  private von: string | null = null;
  private listenId: number | null = null;

  /** Filter der SPS-Prüfung aus der URL – bestimmt Nachbarn und den Rückweg. */
  private filter: SpsSignalFilter | null = null;
  private filterParams: Params = {};

  private subs = new Subscription();

  loescht = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: SpsPruefungApiService,
    private authService: AuthService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.subs.add(
      this.route.paramMap.subscribe(params => {
        this.projektId = Number(params.get('projektId')) || 0;
        this.signalId = Number(params.get('signalId')) || 0;

        const q = this.route.snapshot.queryParamMap;
        this.von = q.get('von');
        this.listenId = Number(q.get('listenId')) || null;

        if (this.von === 'liste' && this.listenId) {
          // Aus einer SPS-Liste geöffnet: die Query-Parameter sind der Listenfilter,
          // nicht der Prüfungsfilter – kein Vorheriges/Nächstes der Prüfung.
          this.filter = null;
          this.filterParams = {};
        } else {
          this.filter = spsFilterAusParams(q, this.projektId);
          this.filterParams = spsFilterAlsParams(this.filter);
        }

        this.laden();
      }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get canEdit(): boolean {
    return this.authService.canEdit();
  }


  // =========================================================
  // LADEN
  // =========================================================

  laden(): void {
    if (!this.signalId) {
      this.fehler = 'Ungültige Signal-Id.';
      return;
    }

    this.laedt = true;
    this.fehler = '';
    this.speicherFehler = '';
    this.versionskonflikt = false;

    this.api.getSignal(this.signalId)
      .pipe(finalize(() => this.laedt = false))
      .subscribe({
        next: signal => {
          this.signal = signal;
          this.entwurfStatus = signal.pruefstatus;
          this.entwurfBemerkung = signal.bemerkung ?? '';
          this.ladeVerlauf();
          this.ladeNachbarn();
        },
        error: err => {
          this.signal = null;
          this.fehler = err?.status === 404
            ? 'Das Signal existiert nicht mehr oder ist nicht zugänglich – möglicherweise wurde seine Liste gelöscht.'
            : SpsPruefungApiService.fehlertext(err, 'Das Signal konnte nicht geladen werden.');
        }
      });
  }

  ladeVerlauf(): void {
    this.laedtVerlauf = true;

    this.api.getVerlauf(this.signalId)
      .pipe(finalize(() => this.laedtVerlauf = false))
      .subscribe({
        // Neueste Änderung zuerst – so liest sich ein Protokoll am Prüfplatz.
        next: verlauf => this.verlauf = [...verlauf].reverse(),
        error: () => this.verlauf = []
      });
  }

  private ladeNachbarn(): void {
    if (!this.filter) {
      this.nachbarn = null;
      return;
    }

    this.api.getNachbarn(this.signalId, this.filter).subscribe({
      next: n => this.nachbarn = n,
      error: () => this.nachbarn = null
    });
  }


  // =========================================================
  // PRÜFUNG
  // =========================================================

  get bemerkungPflicht(): boolean {
    return this.entwurfStatus === 'NichtOk';
  }

  get geaendert(): boolean {
    return !!this.signal
      && (this.entwurfStatus !== this.signal.pruefstatus
        || this.entwurfBemerkung.trim() !== (this.signal.bemerkung ?? '').trim());
  }

  get bemerkungFehlt(): boolean {
    return this.bemerkungPflicht && this.entwurfBemerkung.trim().length === 0;
  }

  get kannSpeichern(): boolean {
    return this.canEdit && this.geaendert && !this.speichert && !this.bemerkungFehlt && !this.laedt;
  }

  statusWaehlen(status: SpsPruefstatus): void {
    if (!this.canEdit || this.speichert) {
      return;
    }

    this.entwurfStatus = status;
    this.gespeichertHinweis = '';
  }

  /** Status und Bemerkung gemeinsam mit der aktuellen Version speichern. */
  speichern(danachWeiter = false): void {
    if (!this.signal || !this.kannSpeichern) {
      return;
    }

    const signal = this.signal;
    this.speichert = true;
    this.speicherFehler = '';
    this.gespeichertHinweis = '';
    this.versionskonflikt = false;

    this.api.setStatus(signal.id, {
      status: this.entwurfStatus,
      bemerkung: this.entwurfBemerkung.trim() || null,
      version: signal.version
    })
      .pipe(finalize(() => this.speichert = false))
      .subscribe({
        next: gespeichert => {
          // Kopfdaten sofort übernehmen, Quellen und Verlauf nachladen.
          this.signal = { ...signal, ...gespeichert };
          this.entwurfStatus = gespeichert.pruefstatus;
          this.entwurfBemerkung = gespeichert.bemerkung ?? '';
          this.gespeichertHinweis = `Gespeichert: ${pruefstatusOption(gespeichert.pruefstatus).text}.`;
          this.ladeVerlauf();

          if (danachWeiter && this.nachbarn?.naechstesId) {
            this.zuSignal(this.nachbarn.naechstesId);
          }
        },
        error: err => {
          this.versionskonflikt = SpsPruefungApiService.istVersionskonflikt(err);
          this.speicherFehler = SpsPruefungApiService.fehlertext(err, 'Die Prüfung konnte nicht gespeichert werden.');
        }
      });
  }

  verwerfen(): void {
    if (this.signal) {
      this.entwurfStatus = this.signal.pruefstatus;
      this.entwurfBemerkung = this.signal.bemerkung ?? '';
      this.speicherFehler = '';
      this.gespeichertHinweis = '';
    }
  }


  // =========================================================
  // ANZEIGE
  // =========================================================

  option(status: SpsPruefstatus | null | undefined): SpsPruefstatusOption {
    return pruefstatusOption(status);
  }

  richtung(signal: SpsSignalDetail): string {
    return richtungText(signal.richtung);
  }

  /** Technische Daten als Label/Wert-Liste – fehlende Werte zeigt die Oberfläche als „–“. */
  get technischeFelder(): SpsDetailFeld[] {
    const s = this.signal;

    if (!s) {
      return [];
    }

    return [
      { label: 'Signalname', wert: s.name },
      { label: 'SPS-Adresse', wert: s.adresseNormiert, mono: true },
      { label: 'Adresse (Original)', wert: s.adresseOriginal, mono: true },
      { label: 'Richtung', wert: richtungText(s.richtung) },
      { label: 'Beschreibung', wert: s.beschreibung },
      { label: 'BMK', wert: s.bmk },
      { label: 'SPS-Station / Adressraum', wert: s.stationsSchluessel },
      { label: 'Signaltyp / Datentyp', wert: s.datenTyp },
      // Der eindeutige Anlagenteil der bestätigten Quellen; bei Widerspruch alle Namen.
      { label: 'Anlagenteil', wert: s.anlagenteilName || (s.anlagenteilNamen?.length ? s.anlagenteilNamen.join(' / ') : null) }
    ];
  }


  // =========================================================
  // LÖSCHEN (nur aus Prüfung / vollständig)
  // =========================================================

  signalLoeschen(): void {
    const s = this.signal;

    if (!s || !this.canEdit || this.loescht || this.speichert) {
      return;
    }

    this.loescht = true;

    const data: SpsSignalLoeschenDialogData = {
      signalId: s.id,
      name: s.name,
      adresse: s.adresseNormiert,
      pruefstatus: s.pruefstatus
    };

    this.dialog.open<SpsSignalLoeschenDialogComponent, SpsSignalLoeschenDialogData, SpsLoeschErgebnis | null>(
      SpsSignalLoeschenDialogComponent, { data, width: '600px', maxWidth: '95vw', autoFocus: false }
    ).afterClosed().subscribe(ergebnis => {
      this.loescht = false;

      if (!ergebnis) {
        return;
      }

      this.snackBar.open(SpsPruefungComponent.loeschErgebnisText(s, ergebnis), 'OK', { duration: 6000 });
      this.zurueck();
    });
  }

  listeTitel(q: SpsSignalQuelle): string {
    return listenTypInfo(projektListenTypVonSps(q.listeTyp)).titel;
  }

  listeIcon(q: SpsSignalQuelle): string {
    return listenTypInfo(projektListenTypVonSps(q.listeTyp)).icon;
  }

  /** Felder einer Quelle in der Reihenfolge der Listenart. */
  quellenFelder(q: SpsSignalQuelle): SpsDetailFeld[] {
    const e = q.eintrag;

    const anlagenteil: SpsDetailFeld[] = e.anlagenteilName ? [{ label: 'Anlagenteil', wert: e.anlagenteilName }] : [];

    if (q.listeTyp === 'PlcTags') {
      return [
        { label: 'Ursprünglicher Name', wert: e.signalname },
        { label: 'Adresse', wert: e.adresseOriginal, mono: true },
        { label: 'Beschreibung', wert: e.beschreibung },
        { label: 'Datentyp', wert: e.datenTyp },
        { label: 'Station / Pfad', wert: e.stationsName },
        { label: 'Path', wert: e.platzierung },
        ...anlagenteil,
        { label: 'BMK', wert: e.bmk }
      ];
    }

    return [
      { label: 'Ursprünglicher Name', wert: e.signalname },
      { label: 'Adresse', wert: e.adresseOriginal, mono: true },
      { label: 'Beschreibung', wert: e.beschreibung },
      { label: 'BMK', wert: e.bmk },
      { label: 'Station', wert: e.stationsName },
      { label: 'Anschluss / Kanal', wert: [e.anschluss, e.kanal].filter(x => !!x).join(' / ') || null },
      { label: 'Ort / Platzierung', wert: [e.ort, e.platzierung].filter(x => !!x).join(' / ') || null },
      ...anlagenteil
    ];
  }

  get positionText(): string {
    const n = this.nachbarn;

    if (!n || n.gesamt === 0) {
      return '';
    }

    return n.position > 0 ? `${n.position} von ${n.gesamt}` : `nicht mehr in der Auswahl (${n.gesamt})`;
  }

  trackByProtokoll(_index: number, p: SpsPruefprotokoll): number {
    return p.id;
  }

  trackByQuelle(_index: number, q: SpsSignalQuelle): number {
    return q.eintragId;
  }


  // =========================================================
  // NAVIGATION
  // =========================================================

  get hatVorheriges(): boolean {
    return !!this.nachbarn?.vorherigesId;
  }

  get hatNaechstes(): boolean {
    return !!this.nachbarn?.naechstesId;
  }

  vorheriges(): void {
    if (this.nachbarn?.vorherigesId) {
      this.zuSignal(this.nachbarn.vorherigesId);
    }
  }

  naechstes(): void {
    if (this.nachbarn?.naechstesId) {
      this.zuSignal(this.nachbarn.naechstesId);
    }
  }

  private zuSignal(signalId: number): void {
    void this.router.navigate(['/projekte', this.projektId, 'sps-signale', signalId], {
      queryParams: this.route.snapshot.queryParams
    });
  }

  zurueck(): void {
    if (this.von === 'liste' && this.listenId) {
      // Den Listenzustand (Filter, Seite, Spaltenansicht) unverändert zurückgeben.
      const { von: _von, listenId: _listenId, ...listenParams } = this.route.snapshot.queryParams;
      void this.router.navigate(['/projekte', this.projektId, 'sps-listen', this.listenId], { queryParams: listenParams });
      return;
    }

    if (this.von === 'pruefung' || this.filter) {
      void this.router.navigate(['/projekte', this.projektId, 'sps-pruefung'], { queryParams: this.filterParams });
      return;
    }

    void this.router.navigate(['/projekte', this.projektId]);
  }

  /** Filter der SPS-Prüfung für Links (Brotkrumen) – derselbe Stand wie beim Öffnen. */
  get filterParamsFuerLink(): Params {
    return this.filterParams;
  }

  get zurueckText(): string {
    if (this.von === 'liste' && this.listenId) {
      return 'Zurück zur SPS-Liste';
    }

    return 'Zurück zur SPS-Prüfung';
  }

  zurPruefung(): void {
    void this.router.navigate(['/projekte', this.projektId, 'sps-pruefung'], { queryParams: this.filterParams });
  }

  oeffneListe(listeId: number): void {
    void this.router.navigate(['/projekte', this.projektId, 'sps-listen', listeId]);
  }
}
